/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PrintIcon from "@material-ui/icons/Print";
import CloseIcon from "@material-ui/icons/Close";
import AspectRatioIcon from "@material-ui/icons/AspectRatio";
import moment from "moment";
import "moment/locale/th";

moment.locale("th");

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    backgroundColor: "#334155",
    padding: 0
  },
  dialogTitleBar: {
    backgroundColor: "#1e293b",
    color: "#ffffff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #475569"
  },
  dialogActionsBar: {
    backgroundColor: "#1e293b",
    padding: "10px 24px",
    borderTop: "1px solid #475569",
    display: "flex",
    justifyContent: "space-between"
  },
  scrollContainer: {
    padding: "24px 16px",
    backgroundColor: "#475569",
    display: "flex",
    justifyContent: "center",
    minHeight: "75vh"
  },
  a4Sheet: {
    backgroundColor: "#ffffff",
    color: "#0f172a",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    padding: "20mm 15mm",
    margin: "0 auto",
    fontFamily: '"Sarabun", "TH Sarabun New", "Tahoma", sans-serif',
    boxSizing: "border-box",
    transition: "width 0.2s ease-in-out"
  },
  sheetLandscape: {
    width: "297mm",
    maxWidth: "100%",
    minHeight: "210mm"
  },
  sheetPortrait: {
    width: "210mm",
    maxWidth: "100%",
    minHeight: "297mm"
  },
  reportHeader: {
    textAlign: "center",
    marginBottom: "16px"
  },
  hospitalName: {
    fontSize: "16pt",
    fontWeight: "bold",
    marginBottom: "2px"
  },
  reportMainTitle: {
    fontSize: "14pt",
    fontWeight: "bold",
    marginBottom: "4px"
  },
  reportSubTitle: {
    fontSize: "12pt",
    color: "#334155",
    marginBottom: "8px"
  },
  reportMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "10pt",
    color: "#475569",
    borderBottom: "1px solid #cbd5e1",
    paddingBottom: "6px",
    marginBottom: "12px"
  },
  printTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #334155",
    "& th": {
      backgroundColor: "#f1f5f9",
      color: "#0f172a",
      fontWeight: "bold",
      fontSize: "10pt",
      border: "1px solid #64748b",
      padding: "5px 6px",
      textAlign: "center"
    },
    "& td": {
      border: "1px solid #94a3b8",
      fontSize: "9.5pt",
      padding: "4px 6px",
      color: "#0f172a"
    }
  },
  sumRow: {
    backgroundColor: "#f8fafc",
    fontWeight: "bold",
    "& td": {
      borderTop: "2px solid #334155",
      borderBottom: "2px solid #334155",
      fontWeight: "bold"
    }
  },
  signatureSection: {
    marginTop: "36px",
    display: "flex",
    justifyContent: "space-around",
    pageBreakInside: "avoid",
    breakInside: "avoid"
  },
  signBlock: {
    textAlign: "center",
    width: "240px",
    fontSize: "11pt"
  },
  signSpacer: {
    height: "40px"
  }
}));

const REPORT_TITLES = [
  "รายงานใบกันเงินที่เบิกจ่ายแล้ว",
  "รายงานใบกันเงินที่ยังไม่เบิกจ่าย / คงค้าง",
  "รายงานสรุปงบประมาณแยกตามกิจกรรม",
  "รายงานสรุปงบประมาณแยกตามหมวดหมู่ค่าใช้จ่าย"
];

export default function BudgetReportPrintModal({
  open,
  onClose,
  reportType = 0,
  year = "",
  fromDate = "",
  toDate = "",
  data = []
}) {
  const classes = useStyles();

  // Reports 0 and 1 have 9-10 columns -> default landscape. Reports 2 and 3 -> default portrait or landscape.
  const [orientation, setOrientation] = useState(() =>
    reportType <= 1 ? "landscape" : "portrait"
  );

  useEffect(() => {
    setOrientation(reportType <= 1 ? "landscape" : "portrait");
  }, [reportType]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("budget-report-printing");
    return () => {
      document.body.classList.remove("budget-report-printing");
    };
  }, [open]);

  const toggleOrientation = () => {
    setOrientation(prev => (prev === "landscape" ? "portrait" : "landscape"));
  };

  const handlePrint = () => {
    window.print();
  };

  const formatThaiDate = dateStr => {
    if (!dateStr) return "-";
    const m = moment(dateStr);
    return m.isValid() ? m.add(543, "years").format("DD/MM/YYYY") : dateStr;
  };

  const formatCurrency = num => {
    if (num === null || num === undefined || isNaN(num)) return "0.00";
    return Number(num).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const reportTitle = REPORT_TITLES[reportType] || "รายงานงบประมาณ";

  const dateRangeText =
    fromDate && toDate
      ? ` ตั้งแต่วันที่ ${formatThaiDate(fromDate)} ถึงวันที่ ${formatThaiDate(
          toDate
        )}`
      : fromDate
      ? ` ตั้งแต่วันที่ ${formatThaiDate(fromDate)}`
      : toDate
      ? ` ถึงวันที่ ${formatThaiDate(toDate)}`
      : "";

  const printTimestamp = `${moment()
    .add(543, "years")
    .format("D MMMM YYYY")} เวลา ${moment().format("HH:mm")} น.`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="budget-report-print-dialog"
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Dynamic Print CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 ${orientation};
            margin: 8mm 6mm;
          }
          *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            box-sizing: border-box !important;
          }
          html, body.budget-report-printing {
            height: auto !important;
            min-width: 0 !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }
          body.budget-report-printing > :not(.budget-report-print-dialog) {
            display: none !important;
          }
          .budget-report-print-dialog {
            position: static !important;
            height: auto !important;
            overflow: visible !important;
            display: block !important;
          }
          .budget-report-print-dialog .MuiBackdrop-root,
          .budget-report-print-dialog .budget-report-print-controls {
            display: none !important;
          }
          .budget-report-print-dialog .MuiDialog-container {
            display: block !important;
            position: static !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .budget-report-print-dialog .MuiDialog-paper {
            display: block !important;
            position: static !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            background: #ffffff !important;
            overflow: visible !important;
          }
          .budget-report-print-dialog .budget-print-scroll-container {
            padding: 0 !important;
            background: #ffffff !important;
            display: block !important;
            min-height: 0 !important;
          }
          .budget-report-print-dialog .budget-print-a4-sheet {
            width: 100% !important;
            max-width: 100% !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .budget-report-print-dialog table thead {
            display: table-header-group !important;
            break-inside: avoid !important;
          }
          .budget-report-print-dialog table tr {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .budget-report-print-dialog table th,
          .budget-report-print-dialog table td {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      {/* Screen Controls Header */}
      <DialogTitle
        disableTypography
        className={`${classes.dialogTitleBar} budget-report-print-controls`}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            ตัวอย่างก่อนพิมพ์ (Print Preview - A4)
          </Typography>
          <span
            style={{
              backgroundColor: "#0284c7",
              color: "#ffffff",
              padding: "2px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "600"
            }}
          >
            {reportTitle}
          </span>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant="outlined"
            style={{
              color: "#e2e8f0",
              borderColor: "#64748b",
              textTransform: "none"
            }}
            startIcon={<AspectRatioIcon />}
            onClick={toggleOrientation}
          >
            ทิศทาง: {orientation === "landscape" ? "แนวนอน (Landscape)" : "แนวตั้ง (Portrait)"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            พิมพ์เอกสาร
          </Button>

          <Button
            variant="outlined"
            style={{ color: "#ffffff", borderColor: "#64748b" }}
            startIcon={<CloseIcon />}
            onClick={onClose}
          >
            ปิด
          </Button>
        </div>
      </DialogTitle>

      {/* Preview Sheet Container */}
      <DialogContent className={`${classes.scrollContainer} budget-print-scroll-container`}>
        <div
          className={`${classes.a4Sheet} ${
            orientation === "landscape" ? classes.sheetLandscape : classes.sheetPortrait
          } budget-print-a4-sheet`}
        >
          {/* Document Header */}
          <div className={classes.reportHeader}>
            <div className={classes.hospitalName}>
              โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง
            </div>
            <div className={classes.reportMainTitle}>{reportTitle}</div>
            <div className={classes.reportSubTitle}>
              ปีงบประมาณ พ.ศ. {year}
              {dateRangeText}
            </div>
          </div>

          {/* Meta line */}
          <div className={classes.reportMetaRow}>
            <span>จำนวนรายการทั้งหมด: {data.length.toLocaleString()} รายการ</span>
            <span>วันที่พิมพ์: {printTimestamp}</span>
          </div>

          {/* Report 0: ใบกันเบิกจ่ายแล้ว */}
          {reportType === 0 && (
            <Table className={classes.printTable} size="small">
              <TableHead>
                <TableRow>
                  <th style={{ width: "35px" }}>#</th>
                  <th style={{ width: "110px" }}>เลขที่ใบกันเงิน</th>
                  <th>โครงการ</th>
                  <th>หมวดจัดซื้อ</th>
                  <th>บริษัท/ผู้ขาย</th>
                  <th style={{ width: "75px" }}>ลงวันที่</th>
                  <th style={{ width: "75px" }}>วันที่เบิกจ่าย</th>
                  <th style={{ width: "95px", textAlign: "right" }}>ยอดกันเงิน</th>
                  <th style={{ width: "95px", textAlign: "right" }}>ยอดเบิกจ่าย</th>
                  <th style={{ width: "90px", textAlign: "right" }}>คืนโครงการ</th>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item, idx) => (
                    <TableRow key={item.id || idx}>
                      <td align="center">{idx + 1}</td>
                      <td style={{ fontWeight: "bold" }}>{item.number}</td>
                      <td>{item.projectName || "-"}</td>
                      <td>{item.categoryName || "-"}</td>
                      <td>{item.companyName || "-"}</td>
                      <td align="center">{formatThaiDate(item.kDate)}</td>
                      <td align="center">{formatThaiDate(item.datePay)}</td>
                      <td align="right">{formatCurrency(item.protectMoney)}</td>
                      <td align="right" style={{ fontWeight: "bold" }}>
                        {formatCurrency(item.payMoney)}
                      </td>
                      <td align="right">{formatCurrency(item.diffRefund)}</td>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <td colSpan={10} align="center" style={{ padding: "20px" }}>
                      ไม่พบข้อมูล
                    </td>
                  </TableRow>
                )}
                {data.length > 0 && (
                  <TableRow className={classes.sumRow}>
                    <td colSpan={7} align="right">
                      รวมทั้งสิ้น ({data.length.toLocaleString()} รายการ):
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.protectMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.payMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.diffRefund || 0), 0))}
                    </td>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Report 1: ใบกันยังไม่เบิกจ่าย / คงค้าง */}
          {reportType === 1 && (
            <Table className={classes.printTable} size="small">
              <TableHead>
                <TableRow>
                  <th style={{ width: "35px" }}>#</th>
                  <th style={{ width: "115px" }}>เลขที่ใบกันเงิน</th>
                  <th>โครงการ</th>
                  <th>หมวดจัดซื้อ</th>
                  <th>บริษัท/ผู้ขาย</th>
                  <th style={{ width: "80px" }}>ลงวันที่</th>
                  <th style={{ width: "100px", textAlign: "right" }}>ยอดกันเงิน</th>
                  <th style={{ width: "100px", textAlign: "right" }}>เบิกจ่ายแล้ว</th>
                  <th style={{ width: "105px", textAlign: "right" }}>ยอดคงค้างรอเบิก</th>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item, idx) => (
                    <TableRow key={item.id || idx}>
                      <td align="center">{idx + 1}</td>
                      <td style={{ fontWeight: "bold" }}>{item.number}</td>
                      <td>{item.projectName || "-"}</td>
                      <td>{item.categoryName || "-"}</td>
                      <td>{item.companyName || "-"}</td>
                      <td align="center">{formatThaiDate(item.kDate)}</td>
                      <td align="right">{formatCurrency(item.protectMoney)}</td>
                      <td align="right">{formatCurrency(item.totalPaidSoFar)}</td>
                      <td align="right" style={{ fontWeight: "bold" }}>
                        {formatCurrency(item.pendingBalance)}
                      </td>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <td colSpan={9} align="center" style={{ padding: "20px" }}>
                      ไม่มีรายการคงค้างรอเบิกจ่าย
                    </td>
                  </TableRow>
                )}
                {data.length > 0 && (
                  <TableRow className={classes.sumRow}>
                    <td colSpan={6} align="right">
                      รวมทั้งสิ้น ({data.length.toLocaleString()} รายการ):
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.protectMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalPaidSoFar || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.pendingBalance || 0), 0))}
                    </td>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Report 2: สรุปตามกิจกรรม */}
          {reportType === 2 && (
            <Table className={classes.printTable} size="small">
              <TableHead>
                <TableRow>
                  <th style={{ width: "40px" }}>#</th>
                  <th>กิจกรรม</th>
                  <th style={{ width: "120px" }}>จำนวนใบกันเงิน</th>
                  <th style={{ width: "130px", textAlign: "right" }}>ยอดกันเงินรวม (บาท)</th>
                  <th style={{ width: "130px", textAlign: "right" }}>ยอดเบิกจ่ายแล้ว (บาท)</th>
                  <th style={{ width: "130px", textAlign: "right" }}>ยอดคงเหลือรอเบิก (บาท)</th>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item, idx) => (
                    <TableRow key={item.aid || idx}>
                      <td align="center">{idx + 1}</td>
                      <td>{item.activityName || "-"}</td>
                      <td align="center">{item.protectCount || 0}</td>
                      <td align="right">{formatCurrency(item.totalProtectMoney)}</td>
                      <td align="right">{formatCurrency(item.totalPaidMoney)}</td>
                      <td align="right" style={{ fontWeight: "bold" }}>
                        {formatCurrency(item.totalPendingMoney)}
                      </td>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <td colSpan={6} align="center" style={{ padding: "20px" }}>
                      ไม่พบข้อมูล
                    </td>
                  </TableRow>
                )}
                {data.length > 0 && (
                  <TableRow className={classes.sumRow}>
                    <td colSpan={2} align="right">
                      รวมทั้งสิ้น ({data.length.toLocaleString()} กิจกรรม):
                    </td>
                    <td align="center">
                      {data.reduce((acc, c) => acc + (c.protectCount || 0), 0).toLocaleString()}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalProtectMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalPaidMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalPendingMoney || 0), 0))}
                    </td>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Report 3: สรุปตามหมวดหมู่ค่าใช้จ่าย */}
          {reportType === 3 && (
            <Table className={classes.printTable} size="small">
              <TableHead>
                <TableRow>
                  <th style={{ width: "40px" }}>#</th>
                  <th>หมวดหมู่ค่าใช้จ่าย</th>
                  <th style={{ width: "120px" }}>จำนวนรายการ</th>
                  <th style={{ width: "150px", textAlign: "right" }}>ยอดเงินกันรวม (บาท)</th>
                  <th style={{ width: "150px", textAlign: "right" }}>ยอดเบิกจ่ายแล้ว (บาท)</th>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((item, idx) => (
                    <TableRow key={item.cid || idx}>
                      <td align="center">{idx + 1}</td>
                      <td>{item.categoryName || "-"}</td>
                      <td align="center">{item.protectCount || 0}</td>
                      <td align="right">{formatCurrency(item.totalProtectMoney)}</td>
                      <td align="right" style={{ fontWeight: "bold" }}>
                        {formatCurrency(item.totalPaidMoney)}
                      </td>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <td colSpan={5} align="center" style={{ padding: "20px" }}>
                      ไม่พบข้อมูล
                    </td>
                  </TableRow>
                )}
                {data.length > 0 && (
                  <TableRow className={classes.sumRow}>
                    <td colSpan={2} align="right">
                      รวมทั้งสิ้น ({data.length.toLocaleString()} หมวดหมู่):
                    </td>
                    <td align="center">
                      {data.reduce((acc, c) => acc + (c.protectCount || 0), 0).toLocaleString()}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalProtectMoney || 0), 0))}
                    </td>
                    <td align="right">
                      {formatCurrency(data.reduce((acc, c) => acc + (c.totalPaidMoney || 0), 0))}
                    </td>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Signatures Section */}
          <div className={classes.signatureSection}>
            <div className={classes.signBlock}>
              <div className={classes.signSpacer} />
              <div>( .................................................... )</div>
              <div style={{ marginTop: "4px" }}>ผู้จัดทำรายงาน</div>
              <div style={{ marginTop: "4px", fontSize: "10pt", color: "#64748b" }}>
                วันที่ ........ / ........ / ................
              </div>
            </div>
            <div className={classes.signBlock}>
              <div className={classes.signSpacer} />
              <div>( .................................................... )</div>
              <div style={{ marginTop: "4px" }}>หัวหน้ากลุ่มงาน/ผู้ตรวจ</div>
              <div style={{ marginTop: "4px", fontSize: "10pt", color: "#64748b" }}>
                วันที่ ........ / ........ / ................
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Screen Controls Footer */}
      <DialogActions className={`${classes.dialogActionsBar} budget-report-print-controls`}>
        <Typography variant="body2" style={{ color: "#94a3b8" }}>
          รูปแบบกระดาษ: A4 {orientation === "landscape" ? "แนวนอน" : "แนวตั้ง"} | แนะนำตั้งค่า Margin: Default หรือ Minimum
        </Typography>
        <div style={{ display: "flex", gap: 10 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            พิมพ์เอกสาร
          </Button>
          <Button
            variant="outlined"
            style={{ color: "#ffffff", borderColor: "#64748b" }}
            onClick={onClose}
          >
            ปิด
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

